import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { useCart, useClearCart } from "@/hooks/useCart";
import { useAuth } from "@/contexts/AuthContext";
import { useCreateOrder, useVerifyPayment } from "@/hooks/useOrders";
import { useRazorpay } from "@/hooks/useRazorpay";
import { useValidatePincode, useCalculateShipping } from "@/hooks/useShipping";
import { CourierPartner, OrderTotals } from "@bookstore/shared";
import {
  formatPrice,
  calculateOrderTotals,
  INDIAN_STATES,
} from "@bookstore/shared";
import { toast } from "sonner";
import { AlertCircle, Loader2, CheckCircle2, MapPin } from "lucide-react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cartWithDetails, totalPrice } = useCart();
  const clearCartMutation = useClearCart();
  const createOrderMutation = useCreateOrder();
  const verifyPaymentMutation = useVerifyPayment();
  const { openRazorpay } = useRazorpay();
  const location = useLocation();
  const appliedCoupon = location.state?.appliedCoupon;

  //  NEW: Shipping hooks
  const validatePincodeMutation = useValidatePincode();
  const calculateShippingMutation = useCalculateShipping();

  const [loading, setLoading] = useState(false);
  const [pincodeValidated, setPincodeValidated] = useState(false);
  const [pincodeDetails, setPincodeDetails] = useState<any>(null);
  const [calculatedShipping, setCalculatedShipping] = useState<any>(null);
  const [hasExistingAddress, setHasExistingAddress] = useState(false);
  const [saveAddress, setSaveAddress] = useState(true);

  const [formData, setFormData] = useState({
    name: user?.displayName || "",
    phone: user?.phoneNumber?.replace("+91", "") || "",
    secondaryPhone: "",
    email: user?.email || "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
  });

  const subtotal = totalPrice;
  
  // 🚧 TEMPORARY: Using hardcoded shipping logic (India Post API not available yet)
  // TODO: When India Post API is ready, this will use real-time calculated shipping from API
  const shippingCharges = calculatedShipping?.tariff?.total_amount || 
                          (subtotal >= 500 ? 0 : 50); // Fallback: Free shipping on orders >= ₹500
  
  const totals: OrderTotals = calculateOrderTotals(
    subtotal, 
    appliedCoupon?.discount || 0, 
    shippingCharges, 
    0 // No GST on books
  );

  const stockIssues = cartWithDetails.filter(
    (item) => item.product.stock === 0 || item.quantity > item.product.stock
  );

  //  NEW: Validate pincode on blur
  const handlePincodeBlur = async () => {
    const pincode = formData.pincode.trim();
    
    if (pincode.length !== 6 || !/^\d{6}$/.test(pincode)) {
      setPincodeValidated(false);
      setPincodeDetails(null);
      return;
    }

    try {
      const result = await validatePincodeMutation.mutateAsync(pincode);
      
      if (result.success) {
        setPincodeValidated(true);
        setPincodeDetails(result);
        
        // Auto-fill city and state
        setFormData(prev => ({
          ...prev,
          city: result.city || prev.city,
          state: result.state || prev.state,
        }));

        toast.success(`Pincode verified: ${result.officeName}`);

        // 🚧 TEMPORARY: Calculate shipping with hardcoded logic
        // TODO: When India Post API is ready, this will fetch real-time shipping rates
        //  Calculate shipping cost
        const shippingResult = await calculateShippingMutation.mutateAsync({
          destinationPincode: pincode,
          itemCount: cartWithDetails.length,
          orderValue: subtotal,
          cod: false, // Prepaid
        });

        if (shippingResult.success) {
          setCalculatedShipping(shippingResult);
        }
      } else {
        setPincodeValidated(false);
        setPincodeDetails(null);
        toast.error('Invalid or unserviceable pincode');
      }
    } catch (error) {
      setPincodeValidated(false);
      setPincodeDetails(null);
    }
  };

  useEffect(() => {
    if (cartWithDetails.length === 0) {
      const timer = setTimeout(() => navigate("/cart"), 500);
      return () => clearTimeout(timer);
    }
  }, [cartWithDetails.length, navigate]);

  // Load default address on mount
  useEffect(() => {
    const loadDefaultAddress = async () => {
      if (!user?.uid) return;

      try {
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const defaultAddress = userData.defaultAddress;
          
          if (defaultAddress) {
            setHasExistingAddress(true);
            setFormData({
              name: defaultAddress.name || user?.displayName || '',
              phone: user?.phoneNumber?.replace("+91", "") || '', // Always use auth phone
              secondaryPhone: defaultAddress.secondaryPhone || '',
              email: user?.email || '',
              addressLine1: defaultAddress.addressLine1,
              addressLine2: defaultAddress.addressLine2 || '',
              city: defaultAddress.city,
              state: defaultAddress.state,
              pincode: defaultAddress.pincode,
            });
            setPincodeValidated(true);
            
            // Trigger shipping calculation
            handlePincodeBlur();
          }
        }
      } catch (error) {
        console.error('Error loading default address:', error);
      }
    };

    loadDefaultAddress();
  }, [user?.uid]);

  // Helper: Save/Update address to user document
  const saveAddressToUser = async () => {
    if (!user?.uid || !saveAddress) return;

    try {
      const addressData = {
        name: formData.name,
        // phone comes from user auth, not saved in address
        secondaryPhone: formData.secondaryPhone || undefined,
        email: formData.email || undefined,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2 || undefined,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
      };

      const db = getFirestore();
      await updateDoc(doc(db, 'users', user.uid), {
        defaultAddress: addressData,
      });

      console.log(hasExistingAddress ? 'Address updated' : 'Address saved to user profile');
    } catch (error) {
      console.error('Error saving address:', error);
      // Don't fail order if address save fails
    }
  };

  if (cartWithDetails.length === 0) {
    return (
      <div className="container-custom py-10 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
        <p className="text-text-secondary">Redirecting to cart...</p>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (stockIssues.length > 0) {
      toast.error("Please fix stock issues before checkout");
      return;
    }

    //  Validate pincode before checkout
    if (!pincodeValidated) {
      toast.error("Please enter a valid pincode");
      return;
    }

    // 🔒 Prevent double submission
    if (loading || createOrderMutation.isPending) {
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        items: cartWithDetails.map((item) => ({
          productId: item.productId,
          title: item.product.title,
          price: item.product.price,
          discountedPrice: item.product.discountedPrice,
          quantity: item.quantity,
          imageUrl: item.product.images[0] || "",
        })),
        shippingAddress: {
          id: '',
          name: formData.name,
          phone: formData.phone,
          secondaryPhone: formData.secondaryPhone || undefined,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          isDefault: false,
        },
        totalAmount: totals.total,
        customerName: formData.name,
        customerPhone: formData.phone,
        customerEmail: formData.email,
        shippingCharges: shippingCharges,
        tax: totals.tax,
        couponCode: appliedCoupon?.code,
        couponId: appliedCoupon?.couponId,
        courierPartner: '' as CourierPartner, //  Use iCarry
        totals: totals,
      };

      const createOrderResult = await createOrderMutation.mutateAsync(orderData);
      // Save address to user profile
      await saveAddressToUser();

      openRazorpay({
        razorpayOrderId: createOrderResult.razorpayOrderId,
        amount: createOrderResult.amount,
        customerName: formData.name,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        orderId: createOrderResult.orderId,
        onSuccess: async (paymentResponse) => {
          try {
            await verifyPaymentMutation.mutateAsync({
              razorpayOrderId: paymentResponse.razorpay_order_id,
              razorpayPaymentId: paymentResponse.razorpay_payment_id,
              razorpaySignature: paymentResponse.razorpay_signature,
            });

            // Send admin email notification
            try {
              const functions = getFunctions(undefined, 'asia-south1');
              const sendAdminEmail = httpsCallable(functions, 'sendAdminOrderEmail');
              await sendAdminEmail({ orderId: createOrderResult.orderId });
            } catch (emailError) {
              console.error('Failed to send admin email:', emailError);
              // Don't fail the order if email fails
            }

            toast.success("Payment successful! Order placed.");
            clearCartMutation.mutate();
            navigate(`/order-confirmation?orderId=${createOrderResult.orderId}`);
          } catch (verifyError) {
            console.error("Payment verification failed:", verifyError);
            toast.error("Payment verification failed. Please contact support.");
            navigate(`/orders/${createOrderResult.orderId}`);
          }
        },
        onFailure: async (error, reason) => {
          console.error("Payment failed:", error);
          
          try {
            // Call cloud function to delete the failed order
            const functions = getFunctions(undefined, 'asia-south1');
            const handlePaymentFailureFn = httpsCallable(functions, 'handlePaymentFailure');
            await handlePaymentFailureFn({
              orderId: createOrderResult.orderId,
              failureReason: reason || error?.message || 'Payment failed or cancelled by user',
              deleteOrder: true, // Delete the order completely
            });

            // Show user-friendly error message
            if (reason === 'Payment cancelled by user') {
              toast.error("Payment cancelled. Your order has been removed.");
            } else {
              toast.error("Payment failed. Please try again.");
            }
          } catch (deleteError) {
            console.error("Failed to handle payment failure:", deleteError);
            toast.error("Payment failed. Please contact support if you see duplicate orders.");
          } finally {
            setLoading(false);
          }
        },
      });
    } catch (error) {
      console.error("Order creation failed:", error);
      toast.error("Failed to create order. Please try again.");
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    
    // Reset pincode validation if pincode changes
    if (e.target.name === 'pincode') {
      setPincodeValidated(false);
      setPincodeDetails(null);
      setCalculatedShipping(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Checkout | Bookstore</title>
      </Helmet>

      <div className="container-custom py-10">
        <h1 className="text-3xl font-extrabold tracking-tight text-text mb-8">
          Checkout
        </h1>

        {/* Stock Issues Warning */}
        {stockIssues.length > 0 && (
          <div className="bg-error/10 border border-error/30 p-5 rounded-xl mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-error mt-1" />
              <div>
                <h3 className="font-semibold text-error mb-1">Stock Issues</h3>
                <p className="text-sm text-error/80 mb-2">
                  Please fix these issues before completing checkout:
                </p>
                <ul className="list-disc list-inside text-sm text-error/80 space-y-1">
                  {stockIssues.map((item) => (
                    <li key={item.productId}>
                      {item.product.title}:{" "}
                      {item.product.stock === 0
                        ? "Out of stock"
                        : `Only ${item.product.stock} available`}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/*  Shipping Info Banner */}
        {calculatedShipping && (
          // <div className="bg-green-50 border border-green-200 p-5 rounded-xl mb-8">
          //   <div className="flex items-start gap-3">
          //     <Package className="w-6 h-6 text-green-600 mt-1" />
          //     <div className="flex-1">
          //       <h3 className="font-semibold text-green-900 mb-1">
          //         Standard Shipping {/* 🚧 TEMP: Will show 'India Post' when API ready */}
          //       </h3>
          //       <p className="text-sm text-green-800 mb-2">
          //         Delivery to: {calculatedShipping.deliveryOffice}
          //       </p>
          //       <div className="flex items-center gap-4 text-sm text-green-700">
          //         <span>Weight: {calculatedShipping.tariff?.weight}g</span>
          //         <span>•</span>
          //         <span>Zone: {calculatedShipping.tariff?.zone_description}</span>
          //       </div>
          //     </div>
          //     <div className="text-right">
          //       <div className="text-2xl font-bold text-green-700">
          //         {formatPrice(calculatedShipping.tariff?.total_amount || 0)}
          //       </div>
          //       <div className="text-xs text-green-600">Including GST</div>
          //     </div>
          //   </div>
          // </div>
          <></>
        )}

        <div className="grid lg:grid-cols-3 gap-10">
          {/* LEFT: FORM */}
          <div className="lg:col-span-2 max-sm:max-w-[348px]">
            <form onSubmit={handleSubmit} className="space-y-8 ">
              {/* ADDRESS CARD */}
              <div className="bg-background-soft shadow-sm border border-border rounded-2xl p-6">
                <h2 className="text-xl font-semibold text-text mb-5">
                  Shipping Address
                </h2>

                <div className="grid md:grid-cols-2 gap-5">
                  {/* NAME */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">
                      Full Name*
                    </label>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-primary-600 focus:border-primary-600 outline-none transition"
                    />
                  </div>

                  {/* PHONE */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">
                      Phone*
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      maxLength={10}
                      pattern="[6-9][0-9]{9}"
                      title="Enter a valid 10-digit Indian mobile number"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-primary-600 transition"
                    />
                  </div>

                  {/* SECONDARY PHONE */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">
                      Alternative Phone (Optional)
                    </label>
                    <input
                      name="secondaryPhone"
                      type="tel"
                      maxLength={10}
                      pattern="[6-9][0-9]{9}"
                      title="Enter a valid 10-digit Indian mobile number"
                      value={formData.secondaryPhone}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-primary-600 transition"
                      placeholder="10-digit phone number"
                    />
                  </div>

                  {/* EMAIL */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1 text-text">
                      Email
                    </label>
                    <input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-primary-600 transition"
                    />
                  </div>

                  {/* ADDRESS 1 */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-text mb-1">
                      Address Line 1*
                    </label>
                    <input
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-primary-600 transition"
                    />
                  </div>

                  {/* ADDRESS 2 */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-text mb-1">
                      Address Line 2
                    </label>
                    <input
                      name="addressLine2"
                      value={formData.addressLine2}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-primary-600 transition"
                    />
                  </div>

                  {/* PINCODE -  UPDATED WITH VALIDATION */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-text mb-1">
                      Pincode* {validatePincodeMutation.isPending && <Loader2 className="inline w-4 h-4 animate-spin ml-2" />}
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          name="pincode"
                          maxLength={6}
                          pattern="[0-9]{6}"
                          required
                          value={formData.pincode}
                          onChange={handleChange}
                          onBlur={handlePincodeBlur}
                          className={`
                            w-full px-4 py-2.5 rounded-lg border bg-white 
                            focus:ring-2 focus:ring-primary-600 transition
                            ${pincodeValidated ? 'border-green-500 pr-10' : 'border-border'}
                          `}
                        />
                        {pincodeValidated && (
                          <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handlePincodeBlur}
                        disabled={validatePincodeMutation.isPending || formData.pincode.length !== 6}
                        className="px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {validatePincodeMutation.isPending ? 'Checking...' : 'Check'}
                      </button>
                    </div>
                    {pincodeDetails && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {pincodeDetails.city}, {pincodeDetails.state}
                      </p>
                    )}
                  </div>

                  {/* CITY */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">
                      City*
                    </label>
                    <input
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-primary-600 focus:ring-2 transition"
                      placeholder="Enter city name"
                    />
                  </div>

                  {/* STATE */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">
                      State*
                    </label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-primary-600 focus:ring-2 transition"
                    >
                      <option value="">Select State</option>
                      {INDIAN_STATES.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* SAVE ADDRESS CHECKBOX - Always visible */}
                <div className="mt-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={saveAddress}
                      onChange={(e) => setSaveAddress(e.target.checked)}
                      className="w-4 h-4 rounded border-border text-primary-600 focus:ring-2 focus:ring-primary-600"
                    />
                    <span className="text-sm text-text-secondary">
                      {hasExistingAddress 
                        ? 'Update saved address with these details'
                        : 'Save this address for future orders'
                      }
                    </span>
                  </label>
                </div>
              </div>

              {/* SUBMIT BUTTON */}
              <button
                type="submit"
                disabled={loading || !pincodeValidated || createOrderMutation.isPending || verifyPaymentMutation.isPending}
                className="
                  w-full py-3 rounded-xl
                  bg-primary-600 text-white font-semibold
                  hover:bg-primary-700 active:bg-primary-800
                  transition-transform hover:-translate-y-0.5
                  shadow-md disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {loading || createOrderMutation.isPending || verifyPaymentMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </span>
                ) : !pincodeValidated ? (
                  "Enter Valid Pincode to Continue"
                ) : (
                  "Proceed to Payment"
                )}
              </button>
            </form>
          </div>

          {/* RIGHT: SUMMARY */}
          <div className="lg:col-span-1 max-sm:max-w-[348px]">
            <div className="sticky top-24 bg-background-soft border border-border shadow-lg rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-text mb-4">
                Order Summary
              </h2>

              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto pr-1">
                {cartWithDetails.map((item) => (
                  <div
                    key={item.productId}
                    className="flex justify-between text-sm text-text-secondary"
                  >
                    <span className="truncate">
                      {item.product.title} × {item.quantity}
                    </span>
                    <span className="font-semibold text-text">
                      {formatPrice(
                        (item.product.discountedPrice || item.product.price) *
                          item.quantity
                      )}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-border pt-4 space-y-2 text-text-secondary">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span className="text-text">{formatPrice(subtotal)}</span>
                </div>

                <div className="flex justify-between">
                  <span>Shipping {/* 🚧 TEMP: Will show 'India Post' when API ready */}</span>
                  <span>
                    {shippingCharges === 0 ? (
                      <span className="text-success font-medium">FREE</span>
                    ) : (
                      <span className="text-text">
                        {formatPrice(shippingCharges)}
                        {calculateShippingMutation.isPending && (
                          <Loader2 className="inline w-3 h-3 animate-spin ml-1" />
                        )}
                        {!calculatedShipping && subtotal < 500 && (
                          <span className="text-xs text-gray-500 ml-1">(approx.)</span>
                        )}
                      </span>
                    )}
                  </span>
                </div>

                {/* {(calculatedShipping || totals) && (
                  <div className="flex justify-between text-xs">
                    <span>GST</span>
                    <span className="text-text">
                      {formatPrice(
                        totals.tax
                      )}
                    </span>
                  </div>
                )} */}

                <div className="flex justify-between font-semibold text-lg pt-3 border-t border-border">
                  <span>Total</span>
                  <span className="text-primary-700">
                    {formatPrice(totals.total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutPage;